import * as React from 'react';

import { translate } from '~l10n/client';
import { Divider } from '~shared/components/Divider';
import { IconTimer } from '~shared/components/icons/large/IconTimer/IconTimer';
import { TimerIconState } from '~shared/utils/timerIconState';

interface TimerPopoverContentProps {
  studentName: string;
  iconState: TimerIconState;
  remainingSeconds: number;
  timerDurationSeconds: number;
  startedAt?: string | null;
  finishedAt?: string | null;
}

function formatMinSec(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getTimeInfo(
  iconState: TimerIconState,
  remainingSeconds: number,
  timerDurationSeconds: number
): string {
  const limitText = `${formatMinSec(timerDurationSeconds)} min limit`;

  if (
    iconState === 'completed' ||
    iconState === 'expired' ||
    iconState === 'ended-early'
  ) {
    const usedSeconds = timerDurationSeconds - remainingSeconds;
    return `${formatMinSec(usedSeconds)} min used of ${limitText}`;
  }

  return limitText;
}

function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return (
    date.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) +
    ' at ' +
    date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  );
}

function getStatusLabel(iconState: TimerIconState): string {
  switch (iconState) {
    case 'completed':
      return translate('On time');
    case 'expired':
      return translate('Time expired');
    case 'not-started':
      return translate('Not started');
    case 'ended-early':
      return translate('Ended by teacher');
    case 'paused':
      return translate('Paused');
    default:
      return '';
  }
}

export function TimerPopoverContent({
  studentName,
  iconState,
  remainingSeconds,
  timerDurationSeconds,
  startedAt,
  finishedAt,
}: TimerPopoverContentProps): React.ReactElement {
  const timeInfo = getTimeInfo(
    iconState,
    remainingSeconds,
    timerDurationSeconds
  );
  const statusLabel = getStatusLabel(iconState);
  const showTimestamps = iconState === 'completed' && (startedAt || finishedAt);

  return (
    <div className="flex flex-col gap-5 font-sans text-base w-80">
      <div className="text-xs font-700 text-grey-900 uppercase tracking-wide">
        {studentName}
      </div>
      <div className="text-blue-900">{timeInfo}</div>
      <Divider />
      <div className="flex items-center gap-2">
        <IconTimer state={iconState} size="large" />
        <span className="text-blue-900">{statusLabel}</span>
      </div>
      {showTimestamps && (
        <>
          <Divider />
          <div className="flex flex-col gap-1 text-xs text-blue-600">
            <span>
              {translate('Started')}: {formatDateTime(startedAt)}
            </span>
            <span>
              {translate('Ended')}: {formatDateTime(finishedAt)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
